// Stripe service for web subscriptions
import { loadStripe } from '@stripe/stripe-js';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { firebaseConfig } from '../../firebase.config';

// Initialize Stripe with your publishable key from environment variable
const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

if (!STRIPE_PUBLISHABLE_KEY) {
  console.warn('⚠️ VITE_STRIPE_PUBLISHABLE_KEY not set. Stripe will not work.');
}

let stripePromise = null;

const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
};

class StripeService {
  constructor() {
    this.stripe = null;
    this.functions = null;
  }

  async initialize() {
    try {
      if (!STRIPE_PUBLISHABLE_KEY) {
        console.warn('⚠️ Stripe publishable key not configured');
        return false;
      }
      const app = getApp();
      // Specify region to match your deployed functions (us-central1)
      this.functions = getFunctions(app, 'us-central1');
      this.stripe = await getStripe();
      return true;
    } catch (error) {
      console.error('Error initializing Stripe:', error);
      return false;
    }
  }

  /**
   * Create a checkout session for a subscription
   * @param {string} priceId - Stripe Price ID for the subscription
   * @param {string} mode - 'subscription' or 'payment'
   * @returns {Promise<string>} Checkout session URL
   */
  async createCheckoutSession(priceId, mode = 'subscription') {
    try {
      // Ensure functions is initialized
      if (!this.functions) {
        await this.initialize();
      }

      if (!this.functions) {
        throw new Error('Failed to initialize Firebase Functions');
      }

      const createCheckoutSession = httpsCallable(this.functions, 'createStripeCheckoutSession');
      
      const result = await createCheckoutSession({
        priceId,
        mode,
      });

      if (result.data.error) {
        throw new Error(result.data.error);
      }

      return result.data.url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  }

  /**
   * Redirect to Stripe Checkout
   * @param {string} sessionIdOrUrl - Checkout session ID or full checkout URL
   */
  async redirectToCheckout(sessionIdOrUrl) {
    try {
      // If it's a full URL, redirect directly using window.location
      if (sessionIdOrUrl && (sessionIdOrUrl.startsWith('http://') || sessionIdOrUrl.startsWith('https://'))) {
        window.location.href = sessionIdOrUrl;
        return;
      }

      // Otherwise, use Stripe.js redirectToCheckout with session ID
      const stripe = await getStripe();
      const { error } = await stripe.redirectToCheckout({
        sessionId: sessionIdOrUrl,
      });

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error redirecting to checkout:', error);
      throw error;
    }
  }

  /**
   * Create and redirect to checkout in one call
   * @param {string} priceId - Stripe Price ID
   * @param {string} mode - 'subscription' or 'payment'
   */
  async checkout(priceId, mode = 'subscription') {
    const sessionId = await this.createCheckoutSession(priceId, mode);
    await this.redirectToCheckout(sessionId);
  }

  /**
   * Create a billing portal session for managing subscription
   * @returns {Promise<string>} Billing portal URL
   */
  async createBillingPortalSession() {
    try {
      // Ensure functions is initialized
      if (!this.functions) {
        await this.initialize();
      }

      if (!this.functions) {
        throw new Error('Failed to initialize Firebase Functions');
      }

      const createBillingPortalSession = httpsCallable(this.functions, 'createStripeBillingPortalSession');
      
      const result = await createBillingPortalSession({});

      if (result.data.error) {
        throw new Error(result.data.error);
      }

      return result.data.url;
    } catch (error) {
      console.error('Error creating billing portal session:', error);
      throw error;
    }
  }

  /**
   * Redirect to billing portal
   */
  async redirectToBillingPortal() {
    try {
      const url = await this.createBillingPortalSession();
      window.location.href = url;
    } catch (error) {
      console.error('Error redirecting to billing portal:', error);
      throw error;
    }
  }
}

// Export singleton instance
export default new StripeService();

